/**
 * Input Manager - Handles mouse, keyboard, and touch input for ship control
 */

export class InputManager {
    constructor() {
        // Input state
        this.mouse = {
            position: { x: 0, y: 0 },
            worldPosition: { x: 0, y: 0, z: 0 },
            buttons: { left: false, right: false, middle: false },
            wheel: 0
        };

        this.keyboard = {
            keys: new Set(),
            modifiers: { shift: false, ctrl: false, alt: false }
        };

        this.touch = {
            touches: [],
            isActive: false,
            startPosition: { x: 0, y: 0 }
        };

        this.joystick = {
            enabled: false,
            active: false,
            vector: { x: 0, y: 0 }
        };

        // Callback functions (to be set by Game class)
        this.onMouseMove = null;
        this.onMouseClick = null;
        this.onMouseWheel = null;
        this.onKeyPress = null;
        this.onKeyRelease = null;
        this.onTouchStart = null;
        this.onTouchMove = null;
        this.onTouchEnd = null;

        // Settings
        this.mouseSensitivity = 1.0;
        this.touchSensitivity = 1.0;
        this.preventContextMenu = true;
        this.preventScrolling = true;
        this.uiTouchSelectors = 'button, a, input, select, textarea, label, [data-ui-interactive], #menu, #loading, #hud, #gameOver, #rankingPanel';
        this.lastInputType = 'mouse'; // Track last interaction source

        // Internal state
        this.isEnabled = true;
        this.lastMouseMoveTime = 0;
        this.mouseMoveThrottleMs = 8; // ~120fps for more responsive mouse tracking

        // Bind event handlers
        this.init();
    }

    init() {

        // Mouse events
        document.addEventListener('mousemove', this.handleMouseMove.bind(this));
        document.addEventListener('mousedown', this.handleMouseDown.bind(this));
        document.addEventListener('mouseup', this.handleMouseUp.bind(this));
        document.addEventListener('wheel', this.handleMouseWheel.bind(this));

        // Keyboard events
        document.addEventListener('keydown', this.handleKeyDown.bind(this));
        document.addEventListener('keyup', this.handleKeyUp.bind(this));

        // Touch events
        document.addEventListener('touchstart', this.handleTouchStart.bind(this), { passive: false });
        document.addEventListener('touchmove', this.handleTouchMove.bind(this), { passive: false });
        document.addEventListener('touchend', this.handleTouchEnd.bind(this), { passive: false });

        // Prevent context menu if enabled
        if (this.preventContextMenu) {
            document.addEventListener('contextmenu', this.handleContextMenu.bind(this));
        }

        // Prevent scrolling on mobile if enabled
        if (this.preventScrolling) {
            document.addEventListener('touchmove', this.preventDefaultTouch.bind(this), { passive: false });
        }

        // Window focus/blur events
        window.addEventListener('blur', this.handleWindowBlur.bind(this));

    }

    update() {
        // Called every frame - can be used for input processing that needs frame timing
        // Currently not needed, but available for future use
    }

    // Mouse event handlers
    handleMouseMove(event) {
        this.lastInputType = 'mouse';
        if (!this.isEnabled) return;

        // Throttle mouse move events for performance
        const now = performance.now();
        if (now - this.lastMouseMoveTime < this.mouseMoveThrottleMs) {
            return;
        }
        this.lastMouseMoveTime = now;

        // Update mouse position
        this.mouse.position.x = event.clientX;
        this.mouse.position.y = event.clientY;

        // Call callback if set
        if (this.onMouseMove) {
            this.onMouseMove(this.mouse.position);
        }
    }

    handleMouseDown(event) {
        if (this.isUIEvent(event)) return;
        this.lastInputType = 'mouse';
        if (!this.isEnabled) return;

        // Update button state
        this.updateMouseButtonState(event.button, true);

        // Call callback if set
        if (this.onMouseClick) {
            this.onMouseClick(event.button, 'down', this.mouse.position);
        }

        // Prevent default for game canvas interactions
        event.preventDefault();
    }

    handleMouseUp(event) {
        if (this.isUIEvent(event)) return;
        this.lastInputType = 'mouse';
        if (!this.isEnabled) return;

        // Update button state
        this.updateMouseButtonState(event.button, false);

        // Call callback if set
        if (this.onMouseClick) {
            this.onMouseClick(event.button, 'up', this.mouse.position);
        }
    }

    handleMouseWheel(event) {
        this.lastInputType = 'mouse';
        if (!this.isEnabled) return;

        this.mouse.wheel = event.deltaY;

        // Call callback if set
        if (this.onMouseWheel) {
            this.onMouseWheel(event.deltaY, this.mouse.position);
        }

        // Reset wheel delta
        setTimeout(() => { this.mouse.wheel = 0; }, 100);
    }

    updateMouseButtonState(button, pressed) {
        switch (button) {
            case 0: // Left button
                this.mouse.buttons.left = pressed;
                break;
            case 1: // Middle button
                this.mouse.buttons.middle = pressed;
                break;
            case 2: // Right button
                this.mouse.buttons.right = pressed;
                break;
        }
    }

    // Keyboard event handlers
    handleKeyDown(event) {
        if (!this.isEnabled) return;

        // Update modifier keys
        this.keyboard.modifiers.shift = event.shiftKey;
        this.keyboard.modifiers.ctrl = event.ctrlKey;
        this.keyboard.modifiers.alt = event.altKey;

        // Add key to active keys set
        const key = event.code || event.key;
        if (!this.keyboard.keys.has(key)) {
            this.keyboard.keys.add(key);

            // Call callback if set
            if (this.onKeyPress) {
                this.onKeyPress(this.normalizeKey(key), this.keyboard.modifiers);
            }
        }

        // Prevent default for game keys (numbers 1-5, WASD, space, etc.)
        if (this.isGameKey(key)) {
            event.preventDefault();
        }
    }

    handleKeyUp(event) {
        if (!this.isEnabled) return;

        // Update modifier keys
        this.keyboard.modifiers.shift = event.shiftKey;
        this.keyboard.modifiers.ctrl = event.ctrlKey;
        this.keyboard.modifiers.alt = event.altKey;

        // Remove key from active keys set
        const key = event.code || event.key;
        this.keyboard.keys.delete(key);

        // Call callback if set
        if (this.onKeyRelease) {
            this.onKeyRelease(this.normalizeKey(key), this.keyboard.modifiers);
        }
    }

    // Touch event handlers
    handleTouchStart(event) {
        if (this.isTouchEventOnUI(event)) return;
        this.lastInputType = 'touch';
        if (this.joystick.enabled) {
            if (event.cancelable) event.preventDefault();
            return;
        }
        if (!this.isEnabled) return;

        this.touch.isActive = true;
        this.touch.touches = Array.from(event.touches);

        // Store initial touch position
        if (event.touches.length > 0) {
            this.touch.startPosition.x = event.touches[0].clientX;
            this.touch.startPosition.y = event.touches[0].clientY;
        }

        // Call callback if set
        if (this.onTouchStart) {
            this.onTouchStart(this.touch.touches);
        }

        event.preventDefault();
    }

    handleTouchMove(event) {
        if (this.isTouchEventOnUI(event)) return;
        this.lastInputType = 'touch';
        if (this.joystick.enabled) {
            if (event.cancelable) event.preventDefault();
            return;
        }
        if (!this.isEnabled || !this.touch.isActive) return;

        this.touch.touches = Array.from(event.touches);

        // Convert touch to mouse-like position for ship control
        if (event.touches.length > 0) {
            const touch = event.touches[0];
            this.mouse.position.x = touch.clientX;
            this.mouse.position.y = touch.clientY;

            // Call mouse move callback for ship movement
            if (this.onMouseMove) {
                this.onMouseMove(this.mouse.position);
            }
        }

        // Call touch callback if set
        if (this.onTouchMove) {
            this.onTouchMove(this.touch.touches);
        }

        event.preventDefault();
    }

    handleTouchEnd(event) {
        if (this.isTouchEventOnUI(event)) return;
        this.lastInputType = 'touch';
        if (this.joystick.enabled) {
            if (event.cancelable) event.preventDefault();
            return;
        }
        if (!this.isEnabled) return;

        this.touch.touches = Array.from(event.touches);

        // If no more touches, deactivate
        if (event.touches.length === 0) {
            this.touch.isActive = false;
        }

        // Call callback if set
        if (this.onTouchEnd) {
            this.onTouchEnd(this.touch.touches);
        }

        event.preventDefault();
    }

    // Helper methods
    normalizeKey(key) {
        // Convert key codes to consistent format
        if (key.startsWith('Digit')) {
            return key.replace('Digit', '');
        }
        if (key.startsWith('Key')) {
            return key.replace('Key', '').toLowerCase();
        }
        return key.toLowerCase();
    }

    isGameKey(key) {
        // Define which keys should prevent default behavior
        const gameKeys = [
            'Digit1', 'Digit2', 'Digit3', 'Digit4', 'Digit5',
            'KeyW', 'KeyA', 'KeyS', 'KeyD',
            'Space', 'ShiftLeft', 'ShiftRight'
        ];
        return gameKeys.includes(key);
    }

    handleContextMenu(event) {
        if (this.isUIEvent(event)) return;
        // Prevent right-click context menu
        event.preventDefault();
    }

    isUIEvent(event) {
        const target = event ? event.target : null;
        return this.isInteractiveElement(target || null);
    }

    isTouchEventOnUI(event) {
        if (!event) return false;

        if (this.isInteractiveElement(event.target || null)) {
            return true;
        }

        const touchList = event.touches && event.touches.length ? event.touches : event.changedTouches;
        if (touchList && typeof document !== 'undefined' && typeof document.elementFromPoint === 'function') {
            for (let i = 0; i < touchList.length; i++) {
                const touch = touchList[i];
                const element = document.elementFromPoint(touch.clientX, touch.clientY);
                if (this.isInteractiveElement(element)) {
                    return true;
                }
            }
        }

        return false;
    }

    isInteractiveElement(element) {
        if (!element || typeof element.closest !== 'function') {
            return false;
        }
        return Boolean(element.closest(this.uiTouchSelectors));
    }

    preventDefaultTouch(event) {
        if (this.isTouchEventOnUI(event)) return;
        // Prevent touch scrolling
        event.preventDefault();
    }

    handleWindowBlur() {
        // Clear all input state when window loses focus
        this.mouse.buttons.left = false;
        this.mouse.buttons.right = false;
        this.mouse.buttons.middle = false;
        this.keyboard.keys.clear();
        this.touch.isActive = false;
        this.touch.touches = [];
        this.resetJoystick();
    }

    // Public methods for checking input state
    isKeyPressed(key) {
        const normalizedKey = this.normalizeKey(key);
        return this.keyboard.keys.has(normalizedKey) || this.keyboard.keys.has(key);
    }

    isMouseButtonPressed(button) {
        switch (button) {
            case 0: case 'left': return this.mouse.buttons.left;
            case 1: case 'middle': return this.mouse.buttons.middle;
            case 2: case 'right': return this.mouse.buttons.right;
            default: return false;
        }
    }

    getMousePosition() {
        return { ...this.mouse.position };
    }

    getTouchCount() {
        return this.touch.touches.length;
    }

    enableJoystick(enable = true) {
        this.joystick.enabled = !!enable;
        if (!this.joystick.enabled) {
            this.resetJoystick();
        }
    }

    resetJoystick() {
        this.joystick.vector = { x: 0, y: 0 };
        this.joystick.active = false;
        this.touch.isActive = false;
    }

    setJoystickVector(vector = { x: 0, y: 0 }, active = true) {
        if (!this.joystick.enabled) return;

        const clamp = (value) => Math.max(-1, Math.min(1, typeof value === 'number' ? value : 0));
        const x = clamp(vector.x);
        const y = clamp(vector.y);

        this.joystick.vector = { x, y };
        this.joystick.active = !!active;
        this.touch.isActive = !!active && (Math.abs(x) > 0.001 || Math.abs(y) > 0.001);
        this.lastInputType = 'touch';
    }

    getJoystickVector() {
        return { ...this.joystick.vector };
    }

    isJoystickEnabled() {
        return !!this.joystick.enabled;
    }

    isJoystickActive() {
        return this.joystick.enabled && this.joystick.active;
    }

    // Enable/disable input
    enable() {
        this.isEnabled = true;
    }

    disable() {
        this.isEnabled = false;
        this.handleWindowBlur(); // Clear all input state
    }

    // Dispose of input manager
    dispose() {
        // Remove all event listeners
        document.removeEventListener('mousemove', this.handleMouseMove);
        document.removeEventListener('mousedown', this.handleMouseDown);
        document.removeEventListener('mouseup', this.handleMouseUp);
        document.removeEventListener('wheel', this.handleMouseWheel);
        document.removeEventListener('keydown', this.handleKeyDown);
        document.removeEventListener('keyup', this.handleKeyUp);
        document.removeEventListener('touchstart', this.handleTouchStart);
        document.removeEventListener('touchmove', this.handleTouchMove);
        document.removeEventListener('touchend', this.handleTouchEnd);
        document.removeEventListener('contextmenu', this.handleContextMenu);
        window.removeEventListener('blur', this.handleWindowBlur);

        // Clear callbacks
        this.onMouseMove = null;
        this.onMouseClick = null;
        this.onMouseWheel = null;
        this.onKeyPress = null;
        this.onKeyRelease = null;
        this.onTouchStart = null;
        this.onTouchMove = null;
        this.onTouchEnd = null;

    }
}